"use client";

import { useEffect, useRef, useSyncExternalStore, type ReactNode } from "react";
import type { BreadcrumbItem } from "@/config/routes";

export interface PageAction {
  label: string;
  icon?: ReactNode;
  onClick: () => void;
  variant?: "primary" | "secondary" | "outline" | "default" | "destructive" | "ghost" | "link";
  disabled?: boolean;
  className?: string;
  dataTestId?: string;
}

type PageHeaderStore = {
  owner: symbol | null;
  actions: PageAction[];
};

export type PageHeaderMeta = {
  title?: string;
  description?: string;
  breadcrumbs?: BreadcrumbItem[];
};

type PageHeaderMetaStore = {
  owner: symbol | null;
  meta: PageHeaderMeta | null;
};

const pageHeaderListeners = new Set<() => void>();
let pageHeaderStore: PageHeaderStore = {
  owner: null,
  actions: [],
};
let pageHeaderMetaStore: PageHeaderMetaStore = {
  owner: null,
  meta: null,
};

function emitPageHeaderChange() {
  pageHeaderListeners.forEach((listener) => listener());
}

function subscribeToPageHeader(listener: () => void) {
  pageHeaderListeners.add(listener);
  return () => pageHeaderListeners.delete(listener);
}

function getPageHeaderSnapshot() {
  return pageHeaderStore.actions;
}

function getPageHeaderMetaSnapshot() {
  return pageHeaderMetaStore.meta;
}

function setPageHeaderMeta(owner: symbol, meta: PageHeaderMeta | null) {
  pageHeaderMetaStore = {
    owner,
    meta,
  };
  emitPageHeaderChange();
}

function clearPageHeaderMeta(owner: symbol) {
  if (pageHeaderMetaStore.owner !== owner) return;
  pageHeaderMetaStore = {
    owner: null,
    meta: null,
  };
  emitPageHeaderChange();
}

function setPageHeaderActions(owner: symbol, actions: PageAction[]) {
  pageHeaderStore = {
    owner,
    actions,
  };
  emitPageHeaderChange();
}

function clearPageHeaderActions(owner: symbol) {
  if (pageHeaderStore.owner !== owner) return;
  pageHeaderStore = {
    owner: null,
    actions: [],
  };
  emitPageHeaderChange();
}

export function useRegisterPageHeaderActions(actions: PageAction[]): void {
  const ownerRef = useRef<symbol | null>(null);
  if (!ownerRef.current) {
    ownerRef.current = Symbol("page-header-actions");
  }

  useEffect(() => {
    setPageHeaderActions(ownerRef.current as symbol, actions);
  }, [actions]);

  useEffect(
    () => () => {
      clearPageHeaderActions(ownerRef.current as symbol);
    },
    []
  );
}

export function usePageHeaderActions(): PageAction[] {
  return useSyncExternalStore(subscribeToPageHeader, getPageHeaderSnapshot, getPageHeaderSnapshot);
}

export function useRegisterPageHeaderMeta(meta: PageHeaderMeta | null): void {
  const ownerRef = useRef<symbol | null>(null);
  if (!ownerRef.current) {
    ownerRef.current = Symbol("page-header-meta");
  }

  useEffect(() => {
    setPageHeaderMeta(ownerRef.current as symbol, meta);
  }, [meta]);

  useEffect(
    () => () => {
      clearPageHeaderMeta(ownerRef.current as symbol);
    },
    []
  );
}

export function usePageHeaderMeta(): PageHeaderMeta | null {
  return useSyncExternalStore(subscribeToPageHeader, getPageHeaderMetaSnapshot, getPageHeaderMetaSnapshot);
}

export function hasActivePageHeaderRegistration(): boolean {
  return pageHeaderStore.owner !== null || pageHeaderMetaStore.owner !== null;
}
